// dedupe.ts — Overlay-Dubletten-Dedupe des Kalender-Neubaus (`/termine`, SP3;
// User-Entscheidung 62). Dieselbe Besprechung liegt oft in mehreren überlagerten
// Kalendern (Organisator + eingeladene Kollegen) → ohne Dedupe erscheinen N
// identische Blöcke im Grid (Screenshot-Befund der Review).
//
// Schlüssel ist die Graph-`iCalUId` (postfachübergreifend stabil) — ABER: alle
// Instanzen einer Serie teilen die iCalUId des Serien-Masters, deshalb MUSS der
// Start-Zeitpunkt mit in den Schlüssel (sonst verschwinden Serieninstanzen;
// Vorplan §5 „Dedupe-Falle Serien"). Events ohne iCalUId werden NIE dedupt.
//
// Gewinner = niedrigster Rang laut `rankOf` (CalendarStateContext: eigenes
// Postfach < WRITE-Postfach < Rest); bei Gleichstand gewinnt der zuerst
// gesehene. Die Quellen der Verlierer wandern als `ghostSources` an den
// Gewinner — der Tooltip zeigt sie als „auch in: …"
// (OutlookKalender.tooltipAccessor). Bewusst React-frei → unter vitest testbar.
//
// WICHTIG (Review-Fix zu #784): Der Dedupe gilt NUR für die Overlay-/
// Einzelansicht. Die geteilte Spalten-Ansicht (SharedColumnsView) zeigt je
// Spalte ihr Postfach VOLLSTÄNDIG — sie bekommt die UNgededupten Events,
// sonst verschwinden Termine aus den Verlierer-Spalten. Den Split kapselt
// `deriveViewEvents` (unten), das der CalendarStateContext als einzige
// Ableitungsquelle nutzt.

import type { CalendarGridEvent } from "./types";

type GhostSource = NonNullable<CalendarGridEvent["ghostSources"]>[number];

/** Ghost-Quellen zusammenführen: eindeutig je Adresse, Gewinner-Adresse nie dabei. */
function mergeGhostSources(
    vorhanden: GhostSource[] | undefined,
    neu: GhostSource[],
    gewinnerAddress: string,
): GhostSource[] {
    const out: GhostSource[] = [...(vorhanden ?? [])];
    const hat = (address: string) =>
        out.some((g) => g.address.toLowerCase() === address.toLowerCase());
    for (const g of neu) {
        if (g.address.toLowerCase() === gewinnerAddress.toLowerCase()) continue;
        if (!hat(g.address)) out.push(g);
    }
    return out;
}

/**
 * Overlay-Dubletten auf EINEN Block reduzieren. Reihenfolge der übrigen Events
 * bleibt erhalten (der Gewinner steht an der Position des zuerst gesehenen
 * Duplikats). Die Eingabe-Events werden nicht mutiert.
 *
 * @param rankOf Rang eines Quell-Postfachs (kleiner gewinnt); der Aufrufer
 *               kodiert damit „eigenes Postfach vor WRITE vor Rest".
 */
export function dedupeOverlayEvents(
    events: CalendarGridEvent[],
    rankOf: (mailboxAddress: string) => number,
): CalendarGridEvent[] {
    const result: CalendarGridEvent[] = [];
    const indexByKey = new Map<string, number>();

    for (const ev of events) {
        // Ohne iCalUId (altes Backend / Graph liefert keine) nie dedupen —
        // lieber eine sichtbare Dublette als ein verschwundener Termin.
        if (!ev.iCalUId) {
            result.push(ev);
            continue;
        }
        const key = `${ev.iCalUId}::${ev.start.getTime()}`;
        const idx = indexByKey.get(key);
        if (idx === undefined) {
            indexByKey.set(key, result.length);
            result.push(ev);
            continue;
        }

        const bisher = result[idx];
        if (rankOf(ev.mailbox) < rankOf(bisher.mailbox)) {
            // Neuer Gewinner: bisherigen (samt dessen Ghosts) als Ghost-Quellen übernehmen.
            result[idx] = {
                ...ev,
                ghostSources: mergeGhostSources(
                    ev.ghostSources,
                    [bisher.source, ...(bisher.ghostSources ?? [])],
                    ev.source.address,
                ),
            };
        } else {
            // Bisheriger bleibt Gewinner: Verlierer-Quelle(n) anhängen.
            result[idx] = {
                ...bisher,
                ghostSources: mergeGhostSources(
                    bisher.ghostSources,
                    [ev.source, ...(ev.ghostSources ?? [])],
                    bisher.source.address,
                ),
            };
        }
    }

    return result;
}

// ── Ansicht-Split (Review-Fix zu #784) ─────────────────────────────────────

/** Die aus den rohen Events abgeleiteten Sichten des CalendarStateContext. */
export interface DerivedViewEvents {
    /** Overlay-/Einzelansicht: dedupet, ungefiltert (z. B. Mobile-DaySelector). */
    events: CalendarGridEvent[];
    /** Overlay-/Einzelansicht: dedupet + Art-Filter (TimeGridView/Mobile). */
    filteredEvents: CalendarGridEvent[];
    /**
     * Geteilte Spalten-Ansicht: UNgededupt + Art-Filter (SharedColumnsView).
     * Jede Spalte zeigt ihr Postfach vollständig — der Overlay-Dedupe
     * (Entscheidung 62) darf hier NIE greifen, sonst verschwinden Termine
     * aus den Verlierer-Spalten.
     */
    filteredColumnEvents: CalendarGridEvent[];
}

/**
 * Leitet aus den ROHEN (ungededupten) geladenen Events die Sichten des
 * Kalender-Neubaus ab: Overlay-/Einzelansicht dedupet (Entscheidung 62),
 * die geteilte Spalten-Ansicht bleibt ungededupt.
 *
 * @param istSichtbar Art-Filter-Prädikat (kundentermin/austausch; block/
 *                    abwesenheit immer sichtbar — types.ts-Helfer).
 * @param rankOf     Gewinner-Rang des Overlay-Dedupes (kleiner gewinnt).
 */
export function deriveViewEvents(
    roh: CalendarGridEvent[],
    istSichtbar: (e: CalendarGridEvent) => boolean,
    rankOf: (mailboxAddress: string) => number,
): DerivedViewEvents {
    const deduped = dedupeOverlayEvents(roh, rankOf);
    return {
        events: deduped,
        filteredEvents: deduped.filter(istSichtbar),
        filteredColumnEvents: roh.filter(istSichtbar),
    };
}
